import styles from './Retention.module.css';
import { useForm } from 'react-hook-form';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Input, Tooltip } from '@dsi/react-eds';
import { useTheme } from '../../../hooks/useTheme';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { retentionSettingsService } from '../../../services/retentionSettingsService';
import { useEffect } from 'react';
import { useLoader } from '../../../hooks/useLoader';

interface RetentionInputs {
  ociCleanupVerifiedDays: number;
  ociCleanupUnverifiedDays: number;
}

function Retention() {
  const { theme } = useTheme();
  const { addNotifications } = useSwobNotifications();
  const queryClient = useQueryClient();
  const { showLoader, hideLoader } = useLoader();

  const { data } = useQuery({
    queryKey: ['oci-cleanup-settings'],
    queryFn: async (): Promise<RetentionInputs> => {
      try {
        showLoader();
        const { data } = await retentionSettingsService.get();
        return { ociCleanupVerifiedDays: data.ociCleanupVerifiedDays, ociCleanupUnverifiedDays: data.ociCleanupUnverifiedDays };
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to load retention settings.';
        addNotifications([
          { title: 'Error in Retention Settings', icon: 'icon-triangle-warning', description: message, timestamp: new Date(), color: 'red' }
        ]);
        throw error;
      } finally {
        setTimeout(() => {
          hideLoader();
        }, 500);
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
  });

  const form = useForm<RetentionInputs>({
    //defaultValues: { ociCleanupVerifiedDays: 30, ociCleanupUnverifiedDays: 30 },
    mode: 'onChange'
  });

  useEffect(() => {
    if (data) {
      form.reset(data);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: async (payload: RetentionInputs) => {
      showLoader();
      await retentionSettingsService.save(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oci-cleanup-settings'] });
      addNotifications([
        { title: 'Success', icon: 'icon-success', description: 'Retention settings saved successfully!', timestamp: new Date(), color: 'green' }
      ]);
    },
    onError: (error: unknown) => {
      setTimeout(() => {
        hideLoader();
      }, 500);
      const message = error instanceof Error ? error.message : 'Failed to save retention settings.';
      addNotifications([
        { title: 'Error', icon: 'icon-triangle-warning', description: message, timestamp: new Date(), color: 'red' }
      ]);
    }
  });

  const onSubmit = (data: RetentionInputs) => mutation.mutate(data);

  return (
    <form className={styles.form} autoComplete="off" onSubmit={form.handleSubmit(onSubmit)}>
      <div
        className={`${styles['settings-container']} ${styles['settingsContainerPadding']} ${theme ? styles['settingsContainerLight'] : styles['settingsContainerDark']}`}
      >
        <div className={styles['settings-title-container']}>
          <div className={styles['settingsTitle']}>OCI Cleanup Schedule</div>
          <p className={styles['scanningDescription']}>
            Configure the retention period for verified and unverified artifacts in the OCI registry. Maximum retention is 30 days.
          </p>
        </div>
        <div className={`${styles.formRow} row`}>
          <div className={styles.fieldGroup}>
            <label htmlFor="ociCleanupVerifiedDays" className={styles.whiteText}>
              Verified Artifacts (days) <span className={styles.requiredAsterisk}> *</span>{' '}
              <ReactEDSWrapper component={Tooltip} message="Number of days to retain verified artifacts before cleanup." pointer>
                <span style={{ cursor: 'pointer', marginLeft: 4 }}>
                  <i className="icon icon-info" style={{ fontSize: 16, verticalAlign: 'middle' }}></i>
                </span>
              </ReactEDSWrapper>
            </label>
            <ReactEDSWrapper
              className={form.formState.errors.ociCleanupVerifiedDays ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
              component={Input}
              id="ociCleanupVerifiedDays"
              type="number"
              {...form.register('ociCleanupVerifiedDays', {
                valueAsNumber: true,
                required: 'Verified days is required',
                validate: (v) => Number.isInteger(v) || 'Must be a whole number',
                min: { value: 1, message: 'Minimum is 1 day' },
                max: { value: 30, message: 'Maximum is 30 days' }
              })}
            />
            {form.formState.errors.ociCleanupVerifiedDays && (
              <span className={styles.error}>
                <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
                {form.formState.errors.ociCleanupVerifiedDays.message}
              </span>
            )}
          </div>
          <div className={styles.fieldGroup}>
            <label htmlFor="ociCleanupUnverifiedDays" className={styles.whiteText}>
              Unverified Artifacts (days) <span className={styles.requiredAsterisk}> *</span>{' '}
              <ReactEDSWrapper component={Tooltip} message="Number of days to retain unverified artifacts before cleanup." pointer>
                <span style={{ cursor: 'pointer', marginLeft: 4 }}>
                  <i className="icon icon-info" style={{ fontSize: 16, verticalAlign: 'middle' }}></i>
                </span>
              </ReactEDSWrapper>
            </label>
            <ReactEDSWrapper
              className={form.formState.errors.ociCleanupUnverifiedDays ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
              component={Input}
              id="ociCleanupUnverifiedDays"
              type="number"
              {...form.register('ociCleanupUnverifiedDays', {
                valueAsNumber: true,
                required: 'Unverified days is required',
                validate: (v) => Number.isInteger(v) || 'Must be a whole number',
                min: { value: 1, message: 'Minimum is 1 day' },
                max: { value: 30, message: 'Maximum is 30 days' }
              })}
            />
            {form.formState.errors.ociCleanupUnverifiedDays && (
              <span className={styles.error}>
                <span className={styles.dangerIcon} aria-label="error" role="img">&#9888;</span>
                {form.formState.errors.ociCleanupUnverifiedDays.message}
              </span>
            )}
          </div>
        </div>
        <div className={styles['settingsContainerPadding']}>
          <div className={styles.buttonContainer} style={{ marginTop: '1.5rem' }}>
            <button type="button" className="btn secondary" onClick={() => form.reset()} disabled={mutation.isPending}>
              Cancel
            </button>
            <button type="submit" className="btn primary" disabled={!form.formState.isValid || mutation.isPending}>
              Save
            </button>
          </div>
        </div>
      </div>
    </form>
  );
}

export default Retention;
